import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Monitor, Phone, Code, Settings } from 'lucide-react';

const solutions = [
  {
    icon: Monitor,
    title: 'Web Development',
    desc: 'Moderne, responsieve websites en webapplicaties die uw bedrijf online laten groeien.',
    points: ['Custom CMS oplossingen', 'E-commerce platforms', 'Progressive Web Apps', 'API integraties'],
    color: '#3d56a6',
  },
  {
    icon: Phone,
    title: 'Mobile Development',
    desc: 'Native en cross-platform mobiele apps voor iOS en Android.',
    points: ['React Native & Flutter', 'Native iOS & Android', 'App Store optimalisatie', 'Push notificaties'],
    color: '#f2a100',
  },
  {
    icon: Code,
    title: 'Custom Software',
    desc: 'Op maat gemaakte software die perfect aansluit bij uw bedrijfsprocessen.',
    points: ['Bedrijfsspecifieke applicaties', 'Database oplossingen', 'Legacy modernisatie', 'Cloud-native development'],
    color: '#f5c518',
  },
  {
    icon: Settings,
    title: 'Procesautomatisering',
    desc: 'Automatiseer repetitieve taken en verhoog de efficiëntie van uw organisatie.',
    points: ['Workflow automatisering', 'RPA implementatie', 'Integratie platforms', 'Business process management'],
    color: '#0891b2',
  },
];

const processSteps = [
  { nr: '01', title: 'Analyse', desc: 'We analyseren uw behoeften en definiëren de scope.' },
  { nr: '02', title: 'Ontwerp', desc: 'Onze designers creëren wireframes en prototypes.' },
  { nr: '03', title: 'Ontwikkeling', desc: 'Agile development met regelmatige updates.' },
  { nr: '04', title: 'Testing', desc: 'Uitgebreide testing voor kwaliteit.' },
  { nr: '05', title: 'Launch', desc: 'Zorgvuldige deployment en training.' },
  { nr: '06', title: 'Support', desc: 'Doorlopende ondersteuning na lancering.' },
];

const techStack = [
  'React', 'Vue.js', 'Node.js', '.NET', 'Python', 'Flutter',
  'React Native', 'Azure', 'Docker', 'Laravel',
];

export default function Bedrijfsoplossingen() {
  return (
    <div data-testid="bedrijfsoplossingen-page">
      <section className="relative pt-40 pb-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #16224b 0%, #233a80 58%, #f2a100 100%)' }}>
        <div className="dot-pattern absolute inset-0" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h1 className="text-white font-bold tracking-tight mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.03em' }}>
            Bedrijfsoplossingen op maat
          </h1>
          <p className="text-white/70 max-w-xl mx-auto text-base leading-8">
            Van concept tot lancering - wij ontwikkelen software die uw bedrijf transformeert.
            Web, mobile en custom applicaties die echt werken.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn-gradient">
              Bespreek uw project <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" data-testid="oplossingen-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <h2 className="text-[#16224b] font-bold" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)' }}>
              Software voor elke uitdaging
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((s, i) => (
              <div key={i} className="p-8 rounded-lg border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-5" style={{ background: `${s.color}22` }}>
                  <s.icon className="w-7 h-7" style={{ color: s.color }} />
                </div>
                <h3 className="text-[#16224b] text-lg font-bold mb-3">{s.title}</h3>
                <p className="text-[#6b7184] text-sm leading-7 mb-5">{s.desc}</p>
                <ul className="space-y-2">
                  {s.points.map((point) => (
                    <li key={point} className="text-sm text-[#16224b] flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#f2a100]" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#f8f9fd]" data-testid="proces-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <h2 className="text-[#16224b] font-bold mb-3" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)' }}>
              Van idee tot realisatie
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step) => (
              <div key={step.nr} className="bg-white rounded-lg border border-gray-100 p-7">
                <p className="text-[#f2a100] text-xs font-bold tracking-widest mb-2">{step.nr}</p>
                <h3 className="text-[#16224b] text-lg font-bold mb-2">{step.title}</h3>
                <p className="text-[#6b7184] text-sm leading-7">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" data-testid="tech-stack-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#16224b] font-bold mb-10" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.4rem)' }}>
            Moderne technologieën
          </h2>

          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {techStack.map((tech) => (
              <span
                key={tech}
                className="px-4 py-2 rounded-full bg-[#f8f9fd] border border-gray-100 text-[#16224b] text-sm font-medium"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#f8f9fd] text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-[#16224b] font-bold mb-4" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
            Heeft u een project in gedachten?
          </h2>
          <p className="text-[#6b7184] mb-8">
            Laat ons uw idee omzetten in een werkende oplossing. Neem contact op voor een vrijblijvend gesprek.
          </p>
          <Link to="/contact" className="btn-gradient text-base px-10 py-4">
            Start uw project <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
